import React from 'react';
import { Link } from 'react-router-dom';

const ExperimentalProjects = () => {
  const projects = [
    {
      id: 'workflow-builder',
      title: 'Interactive Workflow Builder',
      description: 'Create and navigate through interactive decision trees and workflows. Build guided processes with branching logic, loops, and visual organization.',
      icon: '🌳',
      features: ['Decision Trees', 'Visual Editor', 'Loop Support', 'Export/Import'],
      status: 'Active',
      link: '/experimental/workflow-builder'
    },
    {
      id: 'data-art-generator',
      title: 'Data Art Generator',
      description: 'Transform datasets into beautiful generative art pieces using p5.js and creative algorithms.',
      icon: '🎨',
      features: ['P5.js Integration', 'Data Visualization', 'Generative Art', 'Export Options'],
      status: 'Coming Soon',
      link: null
    },
    {
      id: 'neural-network-viz',
      title: 'Neural Network Visualizer',
      description: 'Interactive visualization of neural network architectures and training processes.',
      icon: '🧠',
      features: ['Real-time Training', '3D Visualization', 'Architecture Builder', 'Performance Metrics'],
      status: 'Coming Soon',
      link: null
    },
    {
      id: 'heritage-timeline',
      title: 'Heritage Timeline Explorer',
      description: 'Navigate through Scottish heritage data in an interactive timeline format.',
      icon: '🏛️',
      features: ['Timeline Interface', 'Data Integration', 'Historical Context', 'Geographic Mapping'],
      status: 'Planning',
      link: null
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-500/20 text-green-300';
      case 'Coming Soon': return 'bg-yellow-500/20 text-yellow-300';
      case 'Planning': return 'bg-blue-500/20 text-blue-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      {/* Navigation */}
      <nav className="bg-slate-900/90 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              Fabricated.Space
            </Link>
            <Link to="/" className="text-slate-300 hover:text-white transition-colors duration-200">
              ← Back to Portfolio
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            Experimental Projects
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 mb-8 max-w-4xl mx-auto">
            Interactive experiments exploring the intersection of technology, creativity, and human experience. 
            Each project pushes the boundaries of what's possible in digital spaces.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project) => (
              <div key={project.id} className="group relative overflow-hidden rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-4xl">{project.icon}</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4">{project.title}</h3>
                  <p className="text-slate-400 mb-6 leading-relaxed">{project.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">Features:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.features.map((feature, index) => (
                        <span key={index} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {project.link ? (
                      <Link 
                        to={project.link}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white font-semibold hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-blue-500/25"
                      >
                        Launch Project →
                      </Link>
                    ) : (
                      <button 
                        disabled
                        className="inline-flex items-center px-6 py-3 bg-slate-700 text-slate-400 rounded-xl font-semibold cursor-not-allowed"
                      >
                        Coming Soon
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800 mt-20">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400 mb-4">
            © 2025 Niall Brennan - Fabricated.Space
          </p>
          <p className="text-slate-500 text-sm">
            Experimental digital experiences pushing the boundaries of creativity and technology.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ExperimentalProjects;